import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';

import { useViewer } from '@vimeo/viewer';

import type { JsonObject } from './models/event';
import type { PicoX, TrackAdditionalIdentifiers } from './picox';
import { createPicoX } from './picox';
import { deriveIsInGracePeriod, extractSafeViewerInfo } from './utils';

// NOT A SECRET
const IDENTIFICATION_TOKEN = 'c10f1887-7c05-4a9a-9a60-c307825f0f34';

// ---------------------------------------------------------------------------
// Outside-provider sentinel: used when usePico() is called with no ancestor
// PicoProvider. Logs an error always; additionally throws in dev environments.
// ---------------------------------------------------------------------------

const OUTSIDE_PROVIDER_SENTINEL: PicoX = {
  track: async () => {
    const message = 'usePico() was called outside of <PicoProvider>. Track calls will be dropped.';
    console.error(message);
    if (typeof window !== 'undefined' && defaultIsDevelopment()) {
      throw new Error(message);
    }
  },
};

const PicoContext = createContext<PicoX>(OUTSIDE_PROVIDER_SENTINEL);
export const usePico = (): PicoX => useContext(PicoContext);

// ---------------------------------------------------------------------------
// Buffering proxy: queues .track() calls until the real PicoX instance is
// ready, then binds to it and replays the buffer in FIFO order.
// ---------------------------------------------------------------------------

type BufferedCall = [string, JsonObject, TrackAdditionalIdentifiers | undefined];

type ProxyState =
  | { type: 'buffering'; buffer: BufferedCall[] }
  | { type: 'bound'; pico: PicoX }
  | { type: 'error'; error: unknown };

function createBufferingPico(): {
  proxy: PicoX;
  bind: (real: PicoX) => void;
  fail: (error: unknown) => void;
} {
  let state: ProxyState = { type: 'buffering', buffer: [] };

  const proxy: PicoX = {
    track: async (eventName, payload, additionalIdentifiers) => {
      switch (state.type) {
        case 'bound':
          return state.pico.track(eventName, payload, additionalIdentifiers);
        case 'buffering':
          state.buffer.push([eventName, payload, additionalIdentifiers]);
          return;
        case 'error':
          console.warn(`Discarding event due to init error: ${state.error}`);
          return;
      }
    },
  };

  const bind = (real: PicoX) => {
    const pending = state.type === 'buffering' ? state.buffer : [];
    state = { type: 'bound', pico: real };
    for (const [name, payload, ids] of pending) {
      void real.track(name, payload, ids);
    }
  };

  const fail = (error: unknown) => {
    const count = state.type === 'buffering' ? state.buffer.length : 0;
    state = { type: 'error', error };
    if (count > 0) {
      console.warn(`Discarding ${count} buffered PicoX events due to init error: ${error}`);
    }
  };

  return { proxy, bind, fail };
}

/**
 * User and session identifiers for PicoX analytics.
 * These are stable identifiers that identify the user/session.
 */
export type PicoIdentifiers = {
  /** User ID from Vimeo */
  user_id: string | null;
  /** Visitor unique ID (marketing/analytics cookie) */
  vuid: string | null;
  /** Team ID if user is part of a team */
  team_id?: string | null;
  /** Team owner's user ID */
  team_owner_id?: string | null;
  /** Resolved actor: T_{teamOwnerId} for team users, U_{userId} otherwise */
  actor_id?: string | null;
  /** Enterprise organization ID */
  organization_id?: string | null;
};

/**
 * Contextual data for PicoX analytics.
 * These are values that describe the user's current state and may change.
 */
export type PicoContextData = {
  /** Account type: basic, plus, pro, business, enterprise, etc. */
  account_type: string | null;
  /** Whether user is part of a team */
  is_team_user: boolean | null;
  /** Whether user is currently in a free trial */
  is_free_trial: boolean | null;
  /** User's country code */
  country: string | null;
  /** Whether user is on a mobile device */
  is_mobile: boolean | null;
  /** Whether user is currently in payment grace period */
  is_in_grace_period: boolean | null;
};

type PicoProviderProps = {
  children: React.ReactNode;
  // TODO: Remove this once all the other _app.tsx files are updated to use the new identifiers provider
  identifiers?: PicoIdentifiers | null;
  isDevelopment?: boolean;
  syncIntervalMilliseconds?: number;
};

function defaultIsDevelopment(): boolean {
  // Local and CI environments
  const hostname = window.location?.hostname ?? '';
  return hostname === 'vimeo.dev' || hostname.endsWith('.vimeows.com');
}

export const PicoProvider: React.FC<PicoProviderProps> = ({
  children,
  syncIntervalMilliseconds = 1000,
  isDevelopment,
}) => {
  const viewer = useViewer();

  const {
    user_id,
    vuid,
    team_id,
    team_owner_id,
    actor_id,
    organization_id,
    account_type,
    is_team_user,
    is_free_trial,
    country,
    is_mobile,
  } = extractSafeViewerInfo(viewer);

  const hasViewer = viewer !== null && viewer !== undefined;

  const computedIdentifiers = useMemo(
    (): PicoIdentifiers | null =>
      hasViewer
        ? {
            user_id,
            vuid,
            team_id,
            team_owner_id,
            actor_id,
            organization_id,
          }
        : null,
    [user_id, vuid, team_id, team_owner_id, actor_id, organization_id, hasViewer],
  );

  const computedContext = useMemo(
    (): PicoContextData | null =>
      hasViewer
        ? {
            account_type,
            is_team_user,
            is_free_trial,
            country,
            is_mobile,
            // `is_in_grace_period` is sourced from a cookie that the backend
            // can flip mid-session, so it's resolved fresh per-event in
            // `additionalContext` below rather than snapshotted here.
            is_in_grace_period: null,
          }
        : null,
    [account_type, is_team_user, is_free_trial, country, is_mobile, hasViewer],
  );

  // These refs always hold the latest values so that the getter closures passed
  // to createPicoX (which are called at track-time, not at init-time) return
  // up-to-date data without requiring a new PicoX instance.
  const identifiersRef = useRef<PicoIdentifiers | null>(computedIdentifiers);
  const contextRef = useRef<PicoContextData | null>(computedContext);
  identifiersRef.current = computedIdentifiers;
  contextRef.current = computedContext;

  // Buffering proxy: queues .track() calls until createPicoX resolves, then
  // binds to the real instance and replays buffered calls.  Always provided
  // as the context value — after bind it passes through, avoiding a re-render.
  const { proxy, bind, fail } = useMemo(() => createBufferingPico(), []);

  // Initialize a single PicoX instance when the viewer first becomes available.
  // User/context changes are handled transparently through the refs above.
  useEffect(() => {
    if (!hasViewer) {
      return;
    }

    const additionalIdentifiers = () => ({ ...(identifiersRef.current ?? {}) });
    const additionalContext = () => {
      let page: string | null = null;
      if (typeof window !== 'undefined') {
        page = window.location.pathname;
      }
      return {
        page,
        ...(contextRef.current ?? {}),
        // Read fresh per event so backend cookie flips (e.g. payment
        // failure mid-session) are reflected without waiting for an
        // unrelated re-render to refresh `contextRef`.
        is_in_grace_period: deriveIsInGracePeriod(),
      };
    };

    // To avoid AdBlockers, we send events to a random subdomain of vimeo.com
    // and use Fastly to re-route them to PicoX.
    // IMPORTANT: The endpoint has to be blocked in robots.txt:
    // https://github.com/vimeows/vimeo/blob/8dd06dd742eb6dfe0ac2ee2977a531933545409d/views_v6/sitemap/robots_txt.phtml
    // Otherwise, even it's a POST request, Google will still discover it and try to index. We would end up wasting crawl budget.
    const development = isDevelopment || defaultIsDevelopment();
    const endpoint =
      // TODO[film]: Find out why targeting vimeo-staging gives CORS errors
      // Temporarily, we target vimeo.com for both development and production.
      (development ? 'https://vimeo.com/flarepoint' : 'https://vimeo.com/flarepoint') +
      `/${getRandomString(8)}`;

    const loadPicoXClient = async () => {
      try {
        const instance = await createPicoX({
          additionalIdentifiers,
          additionalContext,
          cookieDomain: `.${window.location.hostname.split('.').slice(-2).join('.')}`,
          identificationToken: IDENTIFICATION_TOKEN,
          isDevelopment: development,
          syncIntervalMilliseconds,
          endpoint,
        });
        bind(instance);
      } catch (error) {
        console.error('Error initializing PicoX client', error);
        fail(error);
      }
    };

    void loadPicoXClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasViewer]);

  return <PicoContext.Provider value={proxy}>{children}</PicoContext.Provider>;
};

function getRandomString(length: number): string {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += letters.charAt(Math.floor(Math.random() * letters.length));
  }
  return result;
}
